import { Paths } from 'types/paths.ts'
import Login from 'pages/Auth/Login/Login.tsx'
import { store } from 'store'
import { getProducer, getProducers } from 'store/producer/thunks'
import { getWidgets } from 'store/feedbackWidget/thunks'
import { getInternalUsers } from 'store/internalUser/thunks'
import { getSalesEvents, getSalesParameters, getSalesPartners, getSalesUsers } from 'store/sales/thunks'
import { getUsers } from 'store/user/thunks'

// AUTH ROUTES
export const LOGIN = { path: Paths.LOGIN, element: <Login /> }

// APP ROUTES
export const DATA_DASHBOARD = {
  path: Paths.DATA_DASHBOARD,
  async lazy() {
    const DataDashboard = await import('pages/DataDashboard/DataDashboard.tsx')
    return { Component: DataDashboard.default }
  }
}

export const GOALS_DASHBOARD = {
  path: Paths.GOALS_DASHBOARD,
  async lazy() {
    const GoalsDashboard = await import('pages/GoalsDashboard/GoalsDashboard.tsx')
    return { Component: GoalsDashboard.default }
  }
}

export const PRODUCERS = {
  path: Paths.PRODUCERS,
  loader: () => store.dispatch(getProducers()),
  async lazy() {
    const Producers = await import('pages/Producers/Producers.tsx')
    return { Component: Producers.default }
  }
}

export const PRODUCER = {
  path: Paths.PRODUCER,
  loader: ({ params }: { params: { slug: string } }) => store.dispatch(getProducer(params.slug)),
  async lazy() {
    const Producer = await import('pages/Producer/Producer.tsx')
    return { Component: Producer.default }
  }
}

export const CONTRACTS = {
  path: Paths.CONTRACTS,
  loader: () => store.dispatch(getProducers()),
  async lazy() {
    const Contracts = await import('pages/Contracts/Contracts.tsx')
    return { Component: Contracts.default }
  }
}

export const CONTRACT_CONSUMPTIONS = {
  path: Paths.CONTRACT_CONSUMPTIONS,
  async lazy() {
    const ContractConsumptions = await import('pages/ContractConsumptions/ContractConsumptions')
    return { Component: ContractConsumptions.default }
  }
}

export const MONTHLY_VOLUMES = {
  path: Paths.MONTHLY_VOLUMES,
  async lazy() {
    const MonthlyVolumes = await import('pages/MonthlyVolumes/MonthlyVolumes.tsx')
    return { Component: MonthlyVolumes.default }
  }
}

export const FEEDBACK_WIDGETS = {
  path: Paths.FEEDBACK_WIDGETS,
  loader: () => store.dispatch(getWidgets()),
  async lazy() {
    const FeedbackWidgets = await import('pages/FeedbackWidgets/FeedbackWidgets.tsx')
    return { Component: FeedbackWidgets.default }
  }
}

export const FEEDBACK_WIDGETS_DETAIL = {
  path: Paths.FEEDBACK_WIDGETS_DETAIL,
  loader: () => store.dispatch(getWidgets()),
  async lazy() {
    const FeedbackWidgetDetail = await import('pages/FeedbackWidgets/FeedbackWidgetDetail/FeedbackWidgetDetail.tsx')
    return { Component: FeedbackWidgetDetail.default }
  }
}

export const CREDIT_CHECKS = {
  path: Paths.CREDIT_CHECKS,
  async lazy() {
    const CreditChecks = await import('pages/CreditChecks/CreditChecks.tsx')
    return { Component: CreditChecks.default }
  }
}

export const USERS = {
  path: Paths.USERS,
  loader: () => store.dispatch(getUsers()),
  async lazy() {
    const Users = await import('pages/Users/Users.tsx')
    return { Component: Users.default }
  }
}

export const PASSWORD_RESET = {
  path: Paths.PASSWORD_RESET,
  async lazy() {
    const PasswordReset = await import('pages/PasswordReset/PasswordReset.tsx')
    return { Component: PasswordReset.default }
  }
}

export const INTERNAL_USERS = {
  path: Paths.INTERNAL_USERS,
  loader: () => store.dispatch(getInternalUsers()),
  async lazy() {
    const InternalUsers = await import('pages/InternalUsers/InternalUsers.tsx')
    return { Component: InternalUsers.default }
  }
}

export const EDIT_INTERNAL_USER = {
  path: Paths.EDIT_INTERNAL_USER,
  loader: () => store.dispatch(getInternalUsers()),
  async lazy() {
    const EditUpdateInternalUser = await import('pages/InternalUsers/CreateUpdateUser/CreateUpdateInternalUser.tsx')
    return { Component: EditUpdateInternalUser.default }
  }
}

export const CREATE_INTERNAL_USER = {
  path: Paths.CREATE_INTERNAL_USER,
  loader: () => store.dispatch(getInternalUsers()),
  async lazy() {
    const CreateUpdateInternalUser = await import('pages/InternalUsers/CreateUpdateUser/CreateUpdateInternalUser.tsx')
    return { Component: CreateUpdateInternalUser.default }
  }
}

export const SALES_BULK_CSV_UPLOAD = {
  path: Paths.SALES_BULK_CSV_UPLOAD,
  loader: () => (store.dispatch(getSalesParameters()), store.dispatch(getSalesPartners())),
  async lazy() {
    const BulkCsvUpload = await import('pages/Sales/BulkCsvUpload/BulkCsvUpload.tsx')
    return { Component: BulkCsvUpload.default }
  }
}

export const SALES_EVENTS = {
  path: Paths.SALES_EVENTS,
  loader: () => (store.dispatch(getSalesEvents()), store.dispatch(getSalesPartners()), store.dispatch(getSalesParameters())),
  async lazy() {
    const Events = await import('pages/Sales/Events/Events.tsx')
    return { Component: Events.default }
  }
}

export const CREATE_SALES_EVENT = {
  path: Paths.CREATE_SALES_EVENT,
  loader: () => (store.dispatch(getSalesEvents()), store.dispatch(getSalesPartners()), store.dispatch(getSalesParameters())),
  async lazy() {
    const CreateUpdateEvent = await import('pages/Sales/Events/CreateUpdateEvent/CreateUpdateEvent.tsx')
    return { Component: CreateUpdateEvent.default }
  }
}

export const EDIT_SALES_EVENT = {
  path: Paths.EDIT_SALES_EVENT,
  loader: () => (store.dispatch(getSalesEvents()), store.dispatch(getSalesPartners()), store.dispatch(getSalesParameters())),
  async lazy() {
    const EditUpdateEvent = await import('pages/Sales/Events/CreateUpdateEvent/CreateUpdateEvent.tsx')
    return { Component: EditUpdateEvent.default }
  }
}

export const SALES_PARAMETERS = {
  path: Paths.SALES_PARAMETERS,
  loader: () => store.dispatch(getSalesParameters()),
  async lazy() {
    const Parameters = await import('pages/Sales/Parameters/Parameters.tsx')
    return { Component: Parameters.default }
  }
}

export const CREATE_SALES_PARAMETER = {
  path: Paths.CREATE_SALES_PARAMETER,
  async lazy() {
    const CreateUpdateParameter = await import('pages/Sales/Parameters/CreateUpdateParameter/CreateUpdateParameter.tsx')
    return { Component: CreateUpdateParameter.default }
  }
}

export const EDIT_SALES_PARAMETER = {
  path: Paths.EDIT_SALES_PARAMETER,
  loader: () => store.dispatch(getSalesParameters()),
  async lazy() {
    const EditUpdateParameter = await import('pages/Sales/Parameters/CreateUpdateParameter/CreateUpdateParameter.tsx')
    return { Component: EditUpdateParameter.default }
  }
}

export const SALES_USERS = {
  path: Paths.SALES_USERS,
  loader: () => (store.dispatch(getSalesUsers()), store.dispatch(getSalesPartners())),
  async lazy() {
    const Users = await import('pages/Sales/Users/Users.tsx')
    return { Component: Users.default }
  }
}

export const CREATE_SALES_USER = {
  path: Paths.CREATE_SALES_USER,
  loader: () => (store.dispatch(getSalesUsers()), store.dispatch(getSalesPartners())),
  async lazy() {
    const CreateUser = await import('pages/Sales/Users/CreateUpdateUser/CreateUpdateUser.tsx')
    return { Component: CreateUser.default }
  }
}

export const EDIT_SALES_USER = {
  path: Paths.EDIT_SALES_USER,
  loader: () => (store.dispatch(getSalesUsers()), store.dispatch(getSalesPartners())),
  async lazy() {
    const EditUser = await import('pages/Sales/Users/CreateUpdateUser/CreateUpdateUser.tsx')
    return { Component: EditUser.default }
  }
}

export const ARCHIVED_EMAILS = {
  path: Paths.ARCHIVED_EMAILS,
  async lazy() {
    const ArchivedEmails = await import('pages/ArchivedEmails/ArchivedEmails.tsx')
    return { Component: ArchivedEmails.default }
  }
}

export const EVENTLOG = {
  path: Paths.EVENTLOG,
  async lazy() {
    const Events = await import('pages/Eventlog/Eventlog')
    return { Component: Events.default }
  }
}
